import React, { useState, useContext } from "react";
import { UserContext } from "../context/UserContext"; // Import UserContext
import {
  Container,
  TextField,
  Button,
  Card,
  CardContent,
  Typography,
  Box,
  Alert,
  CircularProgress,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Grid,
} from "@mui/material";
import WorkIcon from "@mui/icons-material/Work";
import BusinessIcon from "@mui/icons-material/Business";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import DescriptionIcon from "@mui/icons-material/Description";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import StarIcon from "@mui/icons-material/Star";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import axios from "axios";
const API_BASE_URL = window.__ENV__?.API_URL || "https://api.cityzoom.in/api";

const PostJob = () => {
  const { user } = useContext(UserContext); // Access the user from context
  const [job, setJob] = useState({
    title: "",
    company: "",
    location: "",
    description: "",
    salary: 0,
    job_type: "",
    premium: false,
  });
  const [submitted, setSubmitted] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const jobTypes = ["Full-time", "Part-time", "Remote", "Internship"];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setJob({
      ...job,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    setSuccessMessage("");
    setErrorMessage("");

    // Check if user is logged in and extract the user ID
    if (!user || !user.id) {
      setErrorMessage("❌ User not logged in.");
      setSubmitted(false);
      return;
    }

    // Log user ID to the console (for debugging)
    console.log("🔑 User ID from context:", user.id); // Logging user ID

    try {
      // Send job posting with user_id
      const response = await axios.post(`${API_BASE_URL}/jobs`, {
        // const response = await axios.post("http://localhost:5000/api/jobs", {
        ...job, // Spread other job details
        user_id: user.id, // Include user_id from context
      });

      console.log("✅ Job posted successfully:", response.data);

      setSuccessMessage(
        `✅ Job Posted: ${response.data.title} at ${response.data.company}`
      );
      setJob({
        title: "",
        company: "",
        location: "",
        description: "",
        salary: "",
        job_type: "",
        premium: false,
      });
    } catch (error) {
      console.error("🔥 Error posting job:", error);
      setErrorMessage("❌ Failed to post job. Please try again.");
    }
    setSubmitted(false);
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 5, mb: 4 }}>
      <Card sx={{ p: 4, boxShadow: 4, borderRadius: 2 }}>
        <CardContent>
          <Typography
            variant="h5"
            color="#42326e"
            fontWeight="bold"
            textAlign="center"
            mb={3}
          >
            📝 Post a Job
          </Typography>

          {successMessage && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {successMessage}
            </Alert>
          )}
          {errorMessage && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {errorMessage}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box display="flex" alignItems="center" gap={1}>
                  <WorkIcon sx={{ color: "#42326e" }} />
                  <TextField
                    fullWidth
                    label="Job Title"
                    name="title"
                    value={job.title}
                    onChange={handleChange}
                    required
                  />
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box display="flex" alignItems="center" gap={1}>
                  <BusinessIcon sx={{ color: "#42326e" }} />
                  <TextField
                    fullWidth
                    label="Company Name"
                    name="company"
                    value={job.company}
                    onChange={handleChange}
                    required
                  />
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box display="flex" alignItems="center" gap={1}>
                  <LocationOnIcon sx={{ color: "#42326e" }} />
                  <TextField
                    fullWidth
                    label="Location"
                    name="location"
                    value={job.location}
                    onChange={handleChange}
                    required
                  />
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box display="flex" alignItems="center" gap={1}>
                  <DescriptionIcon sx={{ color: "#42326e" }} />
                  <TextField
                    fullWidth
                    label="Job Description"
                    name="description"
                    value={job.description}
                    onChange={handleChange}
                    multiline
                    rows={3}
                    required
                  />
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box display="flex" alignItems="center" gap={1}>
                  <CurrencyRupeeIcon sx={{ color: "#42326e" }} />
                  <TextField
                    fullWidth
                    label="Salary (Optional)"
                    name="salary"
                    type="number"
                    value={job.salary}
                    onChange={handleChange}
                  />
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box display="flex" alignItems="center" gap={1}>
                  <AssignmentIndIcon sx={{ color: "#42326e" }} />
                  <TextField
                    select
                    fullWidth
                    label="Job Type"
                    variant="outlined"
                    name="job_type"
                    value={job.job_type}
                    onChange={handleChange}
                    required
                  >
                    {jobTypes.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="premium"
                      checked={job.premium}
                      onChange={handleChange}
                    />
                  }
                  label={
                    <>
                      <StarIcon color="secondary" /> Feature as Premium Job
                      Listing
                    </>
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <Button
                  variant="contained"
                  sx={{
                    mt: 3,
                    backgroundColor: "#42326e",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "black",
                      color: "white",
                    },
                  }}
                  fullWidth
                  type="submit"
                  disabled={submitted}
                >
                  {submitted ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Post Job"
                  )}
                </Button>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
    </Container>
  );
};

export default PostJob;
